import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [tempUserId, setTempUserId] = useState(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('ptc_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('ptc_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    const { access_token, user: userData, requires_mfa, mfa_setup_required } = response.data;
    
    localStorage.setItem('ptc_token', access_token);
    
    if (requires_mfa) {
      setMfaPending(true);
      setTempUserId(userData.id);
      return { requires_mfa: true };
    }
    
    if (mfa_setup_required) {
      setMfaSetupRequired(true);
      setTempUserId(userData.id);
      return { mfa_setup_required: true };
    }
    
    setUser(userData);
    return { success: true };
  };

  const setupMfa = async () => {
    const response = await api.post('/auth/mfa/setup');
    return response.data;
  };

  const verifyMfa = async (token) => {
    const response = await api.post('/auth/mfa/verify', {
      user_id: tempUserId,
      token
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('ptc_token', access_token);
    setUser(userData);
    setMfaPending(false);
    setMfaSetupRequired(false);
    setTempUserId(null);
    
    return { success: true };
  };

  const logout = () => {
    localStorage.removeItem('ptc_token');
    setUser(null);
    setMfaPending(false);
    setMfaSetupRequired(false);
    setTempUserId(null);
  };

  const impersonate = async (userId) => {
    const response = await api.post(`/admin/impersonate/${userId}`);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('ptc_token', access_token);
    setUser({ ...userData, impersonating: true });
  };

  const stopImpersonation = async () => {
    const response = await api.post('/admin/stop-impersonation');
    const { access_token, user: userData } = response.data;
    localStorage.setItem('ptc_token', access_token);
    setUser(userData);
  };

  const value = {
    user,
    loading,
    mfaPending,
    mfaSetupRequired,
    tempUserId,
    login,
    logout,
    setupMfa,
    verifyMfa,
    impersonate,
    stopImpersonation,
    refreshUser: checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
